import React from 'react';
import { useTranslation } from 'react-i18next';
import { MESSAGE_KIND, MESSAGE_ROLE } from 'src/contexts/assistant/constants';
import { AssistantActionDraft } from 'src/modules/assistant/components/AssistantActionDraft';
import { AssistantBubble } from 'src/modules/assistant/components/AssistantBubble';
import { AssistantMarkdown } from 'src/modules/assistant/components/AssistantMarkdown';
import { NextBestActionCard } from 'src/modules/assistant/components/NextBestActionCard';
import { TAssistantMessage } from 'src/modules/assistant/types/message';
import { TMessageActionsProps } from 'src/modules/assistant/types/actionDraft';

interface AssistantMessageProps {
    message: TAssistantMessage;
    actions: TMessageActionsProps;
    formatDateText: (value?: string) => string | undefined;
}

export const AssistantMessage = ({
    message,
    actions,
    formatDateText,
}: AssistantMessageProps) => {
    const { t } = useTranslation();
    const { recommendation } = message;
    const fromUser = message.role === MESSAGE_ROLE.USER;

    const renderContent = () => {
        if (recommendation)
            return (
                <NextBestActionCard
                    channel={actions.channel}
                    draft={
                        actions.recommendationDrafts[recommendation.requestId]
                    }
                    draftHandlers={actions.getDraftHandlers(
                        recommendation.requestId
                    )}
                    execution={message.actionExecution}
                    feedback={message.feedback}
                    formatDateText={formatDateText}
                    onRecalculate={actions.onRecalculate}
                    onSelectOption={(intent) =>
                        actions.onSelectOption(intent, recommendation)
                    }
                    options={actions.getOptions(recommendation)}
                    recalculateDisabled={actions.recalculateDisabled}
                    recommendation={recommendation}
                    showActions={actions.showRecommendationActions(
                        recommendation
                    )}
                    stale={actions.isRecommendationStale(recommendation)}
                />
            );
        if (message.kind === MESSAGE_KIND.NEXT_ACTION)
            return t('Assistant.NextAction');
        if (fromUser) return formatDateText(message.content);
        return (
            <>
                <AssistantMarkdown
                    content={formatDateText(message.content || '') || ''}
                />
                {message.actionDraft &&
                    (actions.canExecuteDrafts || message.actionExecution) && (
                        <AssistantActionDraft
                            action={message.actionDraft}
                            channel={actions.channel}
                            execution={message.actionExecution}
                            feedback={message.feedback}
                            {...actions.getDraftHandlers(message.requestId)}
                        />
                    )}
            </>
        );
    };

    return (
        <AssistantBubble fromUser={fromUser} wide={Boolean(recommendation)}>
            {renderContent()}
        </AssistantBubble>
    );
};
